import React, { useState, useEffect } from 'react';
import { 
    SparklesIcon, 
    TrophyIcon, 
    CalendarIcon, 
    CheckmarkIcon,
    ArrowUpIcon,
    StoreIcon,
    PlusIcon,
    ImageIcon,
    GlobeIcon,
    EmojiIcon,
    SettingsIcon
} from './Icons';
import { getActiveMods, getAllMods } from '../utils/ModRegistry';
import { API_URL, updateUserOnServer } from '../api';
import { useDynamicManifest } from '../hooks/useDynamicManifest';
import { Capacitor } from '@capacitor/core';
import { STORE_ITEMS, CATEGORIES } from '../constants';

interface PimpMyRideProps {
    currentUser: string;
    usersData: any;
    updateUserData: (updater: any) => void;
    appThemeColor: string;
    currentOpacity?: number;
    onNavigate: (page: any) => void;
    isDemo?: boolean;
}

export const PimpMyRide = ({ 
    currentUser, 
    usersData, 
    updateUserData,
    appThemeColor, 
    currentOpacity = 100,
    onNavigate,
    isDemo = false
}: PimpMyRideProps) => {
    const userData = usersData[currentUser];
    const purchasedItems = userData?.purchasedItems || [];
    const activeCustomizations = userData?.activeCustomizations || {};
    const activeMods = userData?.activeCustomizations?.mods || [];
    const totalPoints = userData?.totalPoints || 0;
    
    const [activeCategory, setActiveCategory] = useState<string>('mods');
    const [showLibraryOnly, setShowLibraryOnly] = useState(false);
    const [purchasing, setPurchasing] = useState<string | null>(null);
    const [customItems, setCustomItems] = useState<any[]>(() => {
        const saved = localStorage.getItem('goodlyfe.custom_items');
        return saved ? JSON.parse(saved) : [];
    });
    
    const [selectedManifestMod, setSelectedManifestMod] = useState<any | null>(null);

    useDynamicManifest({
        title: selectedManifestMod?.name || 'GoodLyfe',
        iconUrl: typeof selectedManifestMod?.icon === 'string' ? selectedManifestMod.icon : '/assets/goodieboy.png',
        startUrl: selectedManifestMod ? `/?mod=${selectedManifestMod.id}` : '/',
        themeColor: '#000000',
        isActive: !!selectedManifestMod
    });

    useEffect(() => {
        const handlePublished = (e: any) => {
            const newItem = e.detail;
            setCustomItems(prev => {
                const next = [newItem, ...prev];
                localStorage.setItem('goodlyfe.custom_items', JSON.stringify(next));
                return next;
            });
        };
        window.addEventListener('goodlyfe.item_published', handlePublished);
        return () => window.removeEventListener('goodlyfe.item_published', handlePublished);
    }, []);

    const hexToRgba = (hex: string, op: number) => {
        let c = hex.replace('#', '');
        if (c.length === 3) c = c[0] + c[0] + c[1] + c[1] + c[2] + c[2];
        try {
            const r = parseInt(c.substring(0, 2), 16), g = parseInt(c.substring(2, 4), 16), b = parseInt(c.substring(4, 6), 16);
            return `rgba(${r}, ${g}, ${b}, ${op / 100})`;
        } catch (e) {
            return `rgba(22, 27, 34, ${op / 100})`;
        }
    };

    const handlePurchase = async (item: any) => {
        if (totalPoints < item.price) return alert("Not enough points!");
        if (window.confirm(`Spend ${item.price} points on ${item.name}?`)) {
            const newPurchased = [...purchasedItems, item.id];
            const newPoints = totalPoints - item.price;
            updateUserData((prev: any) => ({ 
                ...prev, 
                [currentUser]: { ...prev[currentUser], purchasedItems: newPurchased, totalPoints: newPoints } 
            }));
            if (!isDemo) await updateUserOnServer(currentUser, { purchasedItems: newPurchased, totalPoints: newPoints }).catch(console.error);
        }
    };

    const handleEquip = async (item: any) => {
        let updates: any;
        if (item.category === 'postcards') {
            updates = { postcardDefault: item.id };
        } else if (item.category === 'mods') {
            const newMods = activeMods.includes(item.id)
                ? activeMods.filter((id: string) => id !== item.id)
                : [...activeMods, item.id];
            updates = { activeCustomizations: { ...activeCustomizations, mods: newMods } };
        } else {
            updates = { activeCustomizations: { ...activeCustomizations, [item.category]: item.id } };
        }
        
        updateUserData((prev: any) => ({ ...prev, [currentUser]: { ...prev[currentUser], ...updates } }));
        if (!isDemo) await updateUserOnServer(currentUser, updates).catch(console.error);
    };

    const handleUnpublish = async (modId: string, e: React.MouseEvent) => {
        e.stopPropagation();
        if (window.confirm("Are you sure you want to unpublish this mod? It will be removed from Pimp My Ride and unequipped.")) {
            const currentCustomMods = userData?.customMods || [];
            const newCustomMods = currentCustomMods.filter((m: any) => m.id !== modId);
            
            let updates: any = { customMods: newCustomMods };
            
            // Unequip if active
            if (activeMods.includes(modId)) {
                updates.activeCustomizations = {
                    ...activeCustomizations,
                    mods: activeMods.filter((id: string) => id !== modId)
                };
            }
            
            updateUserData((prev: any) => ({
                ...prev,
                [currentUser]: { ...prev[currentUser], ...updates }
            }));
            
            if (!isDemo) {
                await updateUserOnServer(currentUser, updates).catch(console.error);
            }
        }
    };

    const handleAddToHomescreen = (mod: any) => {
        if (Capacitor.isNativePlatform()) {
            alert('On the Native App, manage Quick Actions in Global Settings!');
            return;
        }
        setSelectedManifestMod(mod);
        
        const isIos = /iPad|iPhone|iPod/.test(navigator.userAgent) && !(window as any).MSStream;
        if (isIos) {
            alert(`To add ${mod.name} to your home screen: tap the Share icon at the bottom of your Safari browser, then tap "Add to Home Screen".`);
        } else if ((window as any).deferredPrompt) {
            setTimeout(() => {
                if ((window as any).deferredPrompt) {
                    (window as any).deferredPrompt.prompt();
                    (window as any).deferredPrompt.userChoice.then(() => {
                        (window as any).deferredPrompt = null;
                        setSelectedManifestMod(null);
                    });
                }
            }, 100);
        } else {
            alert(`To add ${mod.name} to your home screen, use your browser menu and select "Add to Home screen" or "Install App".`);
        }
    };

    const mods = [
        ...getAllMods(usersData, currentUser).filter(m => m.id !== 'pimp_my_ride'),
        ...(userData?.customMods || []).map((m: any) => ({
            id: m.id,
            name: m.name,
            icon: m.icon,
            description: m.url ? `Custom web app mod: ${m.url}` : 'Custom system mod generated by GoodieBoy.',
            author: currentUser,
            url: m.url
        }))
    ];

    return (
        <div className="max-w-4xl mx-auto p-4 md:p-6 animate-fade-in pb-24">
            {/* Header / Brand */}
            <header className="relative mb-8 p-6 md:p-10 rounded-[2.5rem] md:rounded-[3.5rem] overflow-hidden bg-gradient-to-br from-blue-900/60 to-purple-900/60 border border-white/10 backdrop-blur-2xl shadow-2xl">
                <div className="absolute top-0 right-0 -translate-y-1/4 translate-x-1/4 w-64 h-64 bg-blue-400/20 rounded-full blur-[100px]" />
                <div className="absolute bottom-0 left-0 translate-y-1/4 -translate-x-1/4 w-64 h-64 bg-purple-400/20 rounded-full blur-[100px]" />
                
                <div className="relative flex flex-col md:flex-row items-center gap-6 md:gap-10">
                    <div className="relative">
                        <div className="w-24 h-24 md:w-32 md:h-32 rounded-full border-4 border-white/20 p-1 bg-black/20 overflow-hidden shadow-2xl">
                            <img src="/assets/goodieboy.png" alt="GoodieBoy" className="w-full h-full object-cover" />
                        </div>
                        <div className="absolute -bottom-1 -right-1 bg-yellow-500 text-black p-2 rounded-full shadow-lg scale-90 md:scale-100">
                            <SparklesIcon className="w-5 h-5" />
                        </div>
                    </div>
                    
                    <div className="text-center md:text-left flex-1">
                        <h1 className="text-3xl md:text-5xl font-black italic uppercase tracking-tighter mb-2 text-white">
                            PIMP MY RIDE
                        </h1>
                        <p className="text-blue-100/70 font-medium max-w-lg italic text-xs md:text-sm leading-relaxed">
                            "Everything you need to make your digital life pop. Modules, themes, postcards—it's all here. Don't be shy, spend those hard-earned Life Points!"
                        </p>
                        <div className="mt-6 flex items-center justify-center md:justify-start gap-4">
                            <div className="bg-black/30 backdrop-blur-md px-5 py-2.5 rounded-full border border-white/10 flex items-center gap-2 shadow-lg">
                                <span className="text-yellow-400 font-bold text-lg">{totalPoints}</span>
                                <span className="text-[10px] uppercase font-black tracking-widest text-white/50">Life Points</span>
                            </div>
                        </div>
                    </div>
                </div>
            </header>

            {/* Category Navigation */}
            <div className="mb-8 flex items-center justify-between gap-4">
                <div className="flex gap-2 overflow-x-auto no-scrollbar pb-1 flex-1">
                    <button 
                        onClick={() => setActiveCategory('mods')} 
                        className={`px-5 py-3 rounded-2xl border text-[10px] font-black uppercase whitespace-nowrap transition-all ${activeCategory === 'mods' ? 'bg-white text-black' : 'border-white/5 bg-white/5 text-white/60 hover:bg-white/10'}`}
                    >
                        Modulars
                    </button>
                    {CATEGORIES.filter(c => c.id !== 'mods').map(cat => (
                        <button 
                            key={cat.id} 
                            onClick={() => setActiveCategory(cat.id)} 
                            className={`px-5 py-3 rounded-2xl border text-[10px] font-black uppercase whitespace-nowrap transition-all ${activeCategory === cat.id ? 'bg-white text-black' : 'border-white/5 bg-white/5 text-white/60 hover:bg-white/10'}`}
                        >
                            {cat.name}
                        </button>
                    ))}
                </div>
                <button
                    onClick={() => setShowLibraryOnly(!showLibraryOnly)}
                    className={`flex-shrink-0 px-5 py-3 rounded-2xl border text-[10px] font-black uppercase tracking-widest transition-all ${showLibraryOnly ? 'bg-blue-600 border-blue-400 text-white' : 'border-white/10 bg-white/5 text-white/40'}`}
                >
                    Library
                </button>
            </div>

            {/* Content Grid */}
            <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                {activeCategory === 'mods' ? (
                    // Modular Apps
                    mods.filter(mod => !showLibraryOnly || activeMods.includes(mod.id)).map(mod => {
                        const isInstalled = activeMods.includes(mod.id);
                        return (
                            <div 
                                key={mod.id}
                                className="group relative p-1 rounded-[2.5rem] transition-all hover:scale-[1.02]"
                            >
                                <div className="absolute inset-0 bg-gradient-to-br from-blue-500/30 to-purple-600/30 rounded-[2.5rem] opacity-0 group-hover:opacity-100 transition-opacity blur-lg" />
                                
                                <div className="relative h-full bg-[#161b22]/80 backdrop-blur-xl border border-white/10 rounded-[2.4rem] p-6 flex flex-col shadow-xl">
                                    <div className="flex items-start justify-between mb-4">
                                        <div className="w-16 h-16 rounded-full bg-black/20 overflow-hidden shadow-inner">
                                            {typeof mod.icon === 'string' ? (
                                                <img src={mod.icon} alt="" className="w-full h-full object-cover" />
                                            ) : (
                                                <div className="w-full h-full flex items-center justify-center">
                                                    <mod.icon className="w-8 h-8 text-blue-400" />
                                                </div>
                                            )}
                                        </div>
                                        <div className="text-[10px] font-black text-blue-400/60 uppercase tracking-widest">Modular</div>
                                    </div>
                                    
                                    <h3 className="text-xl font-black italic uppercase tracking-tighter mb-2 text-white">{mod.name}</h3>
                                    <p className="text-[11px] font-medium text-gray-400 line-clamp-2 mb-6 leading-relaxed">
                                        {mod.description}
                                    </p>
                                    
                                    <div className="mt-auto space-y-2">
                                        <button 
                                            onClick={() => handleEquip({ id: mod.id, category: 'mods' })}
                                            disabled={purchasing === mod.id}
                                            className={`w-full py-4 rounded-2xl font-black uppercase tracking-widest text-xs italic border-2 transition-all ${
                                                isInstalled 
                                                ? 'bg-blue-600 border-white text-white shadow-lg shadow-blue-500/20' 
                                                : 'bg-white/10 border-transparent text-white/70 hover:bg-white/20'
                                            }`}
                                        >
                                            {isInstalled ? 'Enabled' : 'Enable'}
                                        </button>
                                        
                                        {!Capacitor.isNativePlatform() && (
                                            <button 
                                                onClick={() => handleAddToHomescreen(mod)}
                                                className="w-full py-3 rounded-2xl font-bold uppercase tracking-widest text-[10px] text-blue-400 border border-blue-400/20 bg-blue-400/5 hover:bg-blue-400/10 transition-all flex items-center justify-center gap-2"
                                            >
                                                <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M4 16v1a3 3 0 003 3h10a3 3 0 003-3v-1m-4-8l-4-4m0 0L8 8m4-4v12" /></svg>
                                                Add to Homescreen
                                            </button>
                                        )}

                                        {mod.author === currentUser && (
                                            <button 
                                                onClick={(e) => handleUnpublish(mod.id, e)}
                                                className="w-full py-2 rounded-2xl font-bold text-[10px] text-red-400 hover:text-red-300 hover:bg-red-400/10 transition-all uppercase tracking-widest"
                                            >
                                                Unpublish
                                            </button>
                                        )}
                                    </div>
                                </div>
                            </div>
                        );
                    })
                ) : (
                    // Store Items
                    [...STORE_ITEMS, ...customItems].filter(item => item.category === activeCategory && (!showLibraryOnly || purchasedItems.includes(item.id) || item.isOwned)).map(item => {
                        const isPurchased = purchasedItems.includes(item.id) || item.isOwned;
                        const isEquipped = item.category === 'postcards'
                            ? (userData?.postcardDefault === item.id)
                            : (activeCustomizations[item.category] === item.id);
                        
                        return (
                            <div 
                                key={item.id}
                                className="group relative p-1 rounded-[2.5rem] transition-all hover:scale-[1.02]"
                            >
                                <div className="absolute inset-0 bg-gradient-to-br from-blue-500/20 to-purple-600/20 rounded-[2.5rem] opacity-0 group-hover:opacity-100 transition-opacity blur-md" />
                                
                                <div className="relative h-full bg-[#161b22]/80 backdrop-blur-xl border border-white/10 rounded-[2.4rem] p-6 flex flex-col shadow-xl">
                                    <div className="flex justify-between items-start mb-4">
                                        <div className="w-12 h-12 rounded-full bg-black/20 overflow-hidden shadow-inner">
                                            {item.previewImage ? (
                                                <img src={item.previewImage} alt="" className="w-full h-full object-cover" />
                                            ) : (
                                                <div className="w-full h-full flex items-center justify-center">
                                                    <SparklesIcon className="w-6 h-6 text-yellow-500/50" />
                                                </div>
                                            )}
                                        </div>
                                        <div className="text-[10px] font-black text-blue-400/80 uppercase tracking-widest">
                                            {item.price === 0 || item.isOwned ? 'OWNED' : `${item.price} PTS`}
                                        </div>
                                    </div>
                                    
                                    <h4 className="text-lg font-black italic uppercase tracking-tighter mb-2 text-white">{item.name}</h4>
                                    <p className="text-[10px] font-bold text-gray-400 uppercase tracking-tight mb-6 line-clamp-2">
                                        {item.description}
                                    </p>
                                    
                                    <button
                                        onClick={() => isPurchased ? handleEquip(item) : handlePurchase(item)}
                                        className={`mt-auto w-full py-4 rounded-2xl font-black uppercase tracking-widest text-xs italic border-2 transition-all ${
                                            isEquipped 
                                            ? 'bg-blue-600 border-white text-white shadow-lg shadow-blue-500/20' 
                                            : 'bg-white/10 border-transparent text-white/70 hover:bg-white/20 hover:border-white/10'
                                        }`}
                                    >
                                        {isEquipped ? 'Equipped' : (isPurchased ? 'Equip' : 'Ball Out')}
                                    </button>
                                </div>
                            </div>
                        );
                    })
                )}
            </div>

            {/* Empty State */}
            {activeCategory === 'mods' && mods.length === 0 && (
                <div className="text-center py-20 opacity-30 italic">
                    No mods found...
                </div>
            )}
        </div>
    );
};
